/**
 * SessionContext — resolves the current user's identity from /auth/me.
 *
 * The identity service reads the idefy_session cookie and returns:
 *   { tenant_id, subject, email, groups, is_admin }
 *
 * All admin pages consume `useSession().tenantId` instead of a hardcoded slug.
 * If the user is not logged in, /auth/me returns 401 and the context redirects
 * to /auth/login.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

export interface SessionUser {
  tenantId:  string
  subject:   string
  email:     string
  groups:    string[]
  isAdmin:   boolean
}

interface SessionContextValue {
  user:    SessionUser | null
  loading: boolean
  error:   string | null
}

const SessionContext = createContext<SessionContextValue>({
  user:    null,
  loading: true,
  error:   null,
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser]       = useState<SessionUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)

  useEffect(() => {
    fetch('/auth/me', { credentials: 'same-origin' })
      .then(async (res) => {
        if (res.status === 401) {
          // Allow unauthenticated access to login + DEV design-system gallery.
          const path = window.location.pathname
          if (path === '/login' || path.startsWith('/design-system')) {
            setLoading(false)
            return
          }
          window.location.href = '/auth/login'
          return
        }
        if (!res.ok) throw new Error(`/auth/me returned ${res.status}`)
        const data = await res.json()
        setUser({
          tenantId: data.tenant_id,
          subject:  data.subject,
          email:    data.email,
          groups:   data.groups ?? [],
          isAdmin:  data.is_admin ?? false,
        })
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  return (
    <SessionContext.Provider value={{ user, loading, error }}>
      {children}
    </SessionContext.Provider>
  )
}

/** Returns the current session. Throws if used outside <SessionProvider>. */
export function useSession(): SessionContextValue {
  return useContext(SessionContext)
}

/** Convenience hook — returns tenantId or '' while loading. */
export function useTenantId(): string {
  return useContext(SessionContext).user?.tenantId ?? ''
}

/** Convenience hook — true if the user has the idefy-admins group. */
export function useIsAdmin(): boolean {
  return useContext(SessionContext).user?.isAdmin ?? false
}
